<template>
<div>
  <div class="train-ai-container">
    <div class="header">
      <h3>Train AI Model</h3>
      <button class="toggle-btn" @click="state.showContent = !state.showContent">
        {{ state.showContent ? '−' : '+' }}
      </button>
    </div>

    <div v-if="state.showContent" class="content">
      <p class="help">Select one of the methods below to send data:</p>
      
      <label v-for="radio in radioButtons" :key="radio.value">
        <input type="radio" v-model="state.selectedMethod" @change="clearFields" :value="radio.value" /> {{ radio.label }}
      </label>
      <br/><br/>

      <div v-if="state.selectedMethod === 'file'">
        <input type="file" @change="handleUpload($event.target.files)" accept=".csv, .txt, .xlsx, .docx, .pdf" multiple />
        <div v-if="state.files.length && state.files.length > 1" class="selected-files">
          <p>Selected files:</p>
          <ul>
            <li v-for="file in state.files" :key="file.name">
              {{ file.name }}
            </li>
          </ul>
        </div>
        <p class="info">* Allowed file types: .csv, .txt, .xlsx, .docx, .pdf</p>
      </div>
      
      <div v-if="state.selectedMethod === 'url'">
        <input type="text" v-model="state.url" placeholder="Enter URL" /><br/><br/>
        <button @click="handleUpload">Preview URL</button>
        
        <div v-if="state.previewData.length > 0" class="preview-container">
          <h5>Preview Data</h5>
          <textarea v-model="state.previewText" rows="15" cols="50"></textarea>
        </div>
      </div>
      
      <div v-if="state.selectedMethod === 'text'">
        <textarea v-model="state.text" placeholder="Write text here"></textarea>
      </div>
      
      <br/>
      <button @click="handleSubmit" :disabled="isButtonDisabled">Train Callboxs AI</button>
    </div>
  </div>

    <div class="s3-info-container">
      <div class="s3-info">
        <h4>
          File Storage
          <span class="storage-info">
            ({{ totalFiles }} {{ totalFiles === 1 ? 'file' : 'files' }})
          </span>
        </h4>
        <div v-if="state.s3Files.length > 0" class="s3-files">
          <table>
            <thead>
              <tr>
                <th>File Name</th>
                <th>Size</th>
                <th>Last Modified</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="file in state.s3Files" :key="file.Key">
                <td>{{ getFileName(file.Key) }}</td>
                <td>{{ formatFileSize(file.Size) }}</td>
                <td>{{ formatDate(file.LastModified) }}</td>
                <td>
                  <button 
                    class="download-btn" 
                    @click="downloadFile(file.Key)"
                    title="Download file"
                  >⬇️</button>
                  <button 
                    class="delete-btn" 
                    @click="confirmDelete(file.Key)"
                    title="Delete file"
                  >🗑️</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="no-files">No files uploaded yet</p>
        <!-- <button class="sync-btn" @click="syncToAIModel">Sync data to AI model</button> -->
      </div>
    </div>
</div>
</template>

<script>
import { reactive, computed, onMounted } from 'vue';
import axios from 'axios';
import * as cheerio from 'cheerio';


export default {
  setup() {
    const state = reactive({
      files: [],
      file: null,
      url: '',
      text: '',
      previewData: [],
      selectedMethod: '',
      previewText: '',
      showContent: false,
      s3Files: [],
      isValidFileType: false,
    });

    const radioButtons = [
      { value: 'file', label: 'Choose File' },
      // { value: 'url', label: 'Enter URL' },
      { value: 'text', label: 'Write Text' }
    ]

    const allowedFileTypes = [
      'text/csv',
      'text/plain',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/pdf'
    ];

    const isButtonDisabled = computed(() => {
      switch (state.selectedMethod) {
        case 'file':
          return !state.files.length || !state.isValidFileType;
        case 'url':
          return !state.previewData.length;
        case 'text':
          return !state.text;
        default:
          return true;
      }
    }); 

    const handleUpload = async (files) => {
      if (state.selectedMethod === 'file' && files) {
        state.files = Array.from(files);
        const invalidFiles = state.files.filter(file => !allowedFileTypes.includes(file.type));
        
        if (invalidFiles.length > 0) {
          alert('Please upload only .csv, .txt, .xlsx, .docx, or .pdf files');
          state.files = state.files.filter(file => allowedFileTypes.includes(file.type));
        }
        
        state.isValidFileType = state.files.length > 0;
        state.file = state.files[0];
      }
      
      if (state.selectedMethod === 'url' && state.url) {
        try {
          await processData(false, state.url);
        } catch (error) {
          alert("Error: " + error.message);
        }
        return;
      }
      
      if (!state.files && !state.url) return;
    };

    const processData = async (file, url) => {
      try {
        if(url){
          const content = await fetchTextFromUrl(url);
          state.previewData = Array(content)
          state.previewText = state.previewData
        }
      } catch (error) {
        alert("Error processing file: " + error.message);
      }
    };

    const fetchTextFromUrl = async (url) => {
      try {
        const response = await axios.get('/api/v1/train-ai-options/fetch-url', {
          params: { url }
        });
        const bodyContent = response.data.bodyContent;
        
        const $ = cheerio.load(bodyContent);
        
        $('script, style, nav, header, footer, iframe, noscript').remove();
        

        $('br').replaceWith('\n');
        $('p, div, h1, h2, h3, h4, h5, h6, li').each((i, elem) => {
          $(elem).append('\n');
        });
        

        let text = $('body')
          .text()
          .replace(/\s+\n/g, '\n')
          .replace(/\n\s+/g, '\n')
          .replace(/\n{3,}/g, '\n\n')
          .replace(/\t/g, '')
          .trim();
        
        text = text.replace(/\.(?=\S)/g, '. ');
        
        return text;
      } catch (error) {
        throw new Error(`Failed to fetch URL: ${error.message}`);
      }
    };

    const handleSubmit = async () => {
      try {
        switch (state.selectedMethod) {
          case 'file':
            if (state.files.length) {
              await Promise.all(state.files.map(file => uploadToS3(file)));
              alert("All files uploaded successfully!", "success");
            }
            break;
          case 'url':
            if (state.previewText) {
              await uploadToS3(state.previewText);
              alert("Uploaded successfully!", "success");
            }
            break;
          case 'text':
            if (state.text) {
              await uploadToS3(state.text);
              alert("Uploaded successfully!", "success");
            }
            break;
        }
        await fetchS3Files();
        clearFields();
        state.selectedMethod = '';
      } catch (error) {
        alert("Failed to upload file(s). Please try again.", "error");
      }
    };

    const uploadToS3 = async (content) => {
      try {
        let filename, fileType, fileContent;

        if (content instanceof File) {
          filename = content.name;
          fileType = content.type;
          fileContent = content;
        } else {
          filename = `data_${Date.now()}.csv`;
          fileType = "text/csv";
          fileContent = content;
        }

        const response = await axios.get("/api/v1/api-s3/get-s3-signed-url", {
          params: {
            filename: filename,
            filetype: fileType
          },
        });

        const { signedUrl } = response.data;
        await axios.put(signedUrl, fileContent, {
          headers: { "Content-Type": fileType },
        });
      } catch (error) {
        alert("Failed to upload file. Please try again.", "error");
      }
    };

    const syncToAIModel = async () => {
      try {
        await startBedrockIngestion();
        alert("Successfully start syncing data to AI model! It might take 2-5 minutes to complete.", "success");
      } catch (error) {
        alert("Failed to sync data to AI model. Please try again.", "error");
      }
    };

    const startBedrockIngestion = async () => {
      try {
        await axios.post("/api/v1/api-bedrock/start-ingestion", {
          description: "Sync all data to Bedrock knowledge base"
        });
      } catch (error) {
        throw error;
      }
    };

    const clearFields = () => {
      state.url = '';
      state.text = '';
      state.files = [];
      state.previewData = [];
      state.previewText = '';
      state.isValidFileType = false;
    };

    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (date) => {
      return new Date(date).toLocaleString();
    };

    const getFileName = (path) => {
      const parts = path.split('/');
      return parts[parts.length - 1];
    };

    const fetchS3Files = async () => {
      try {
        const response = await axios.get("/api/v1/api-s3/list-files");
        state.s3Files = response.data.files;
      } catch (error) {
        alert("Failed to fetch S3 files");
      }
    };

    const downloadFile = async (key) => {
      try {
        const response = await axios.get("/api/v1/api-s3/get-download-url", {
          params: { key }
        });
        
        if (response.data.signedUrl) {
          const link = document.createElement('a');
          link.href = response.data.signedUrl;
          link.setAttribute('download', getFileName(key));
          
          if (key.toLowerCase().endsWith('.txt')) {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
            const xhr = new XMLHttpRequest();
            xhr.open('GET', response.data.signedUrl, true);
            xhr.responseType = 'blob';
            xhr.onload = function() {
              if (xhr.status === 200) {
                const blob = xhr.response;
                const url = window.URL.createObjectURL(blob);
                link.href = url;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              }
            };
            xhr.send();
          } else {
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        } else {
          throw new Error('Failed to get download URL');
        }
      } catch {
        alert('Failed to download file');
      }
    };

    const confirmDelete = async (fileKey) => {
      if (confirm('Are you sure you want to delete this file?')) {
        try {
          await deleteFile(fileKey);
          await fetchS3Files();
          alert('File deleted successfully');
        } catch (error) {
          alert('Failed to delete file');
        }
      }
    };

    const deleteFile = async (key) => {
      await axios.post("/api/v1/api-s3/delete-file", { key });
    };

    const totalFiles = computed(() => {
      return state.s3Files.length;
    });

    onMounted(() => {
      fetchS3Files();
    });

    return {
      state,
      radioButtons,
      isButtonDisabled,
      handleUpload,
      handleSubmit,
      clearFields,
      formatFileSize,
      formatDate,
      fetchS3Files,
      getFileName,
      downloadFile,
      confirmDelete,
      totalFiles,
      syncToAIModel
    };
  },
};
</script>

<style scoped>
h3 {
  text-align: left;
}
.help {
  font-weight: 500;
  margin: 0 0 20px;
  color: #818181;
  font-size: 12px;
}
.info {
  margin: 10px 0 0 0;
  color: #818181;
  font-size: 12px;
}
label {
  margin: 0 10px 20px 0;
}
.preview-container {
  border: 1px solid #ddd;
  padding: 10px;
  margin-top: 20px;
}

textarea, input[type="text"] {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  resize: vertical;
}

.train-ai-container {
  border: 1px solid #ddd;
  border-radius: 4px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  border-bottom: 1px solid #ddd;
}

.header h3 {
  margin: 0;
}

.toggle-btn {
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px 12px;
  cursor: pointer;
  font-size: 16px;
}

.content {
  padding: 20px;
}

.s3-info-container {
  margin-top: 40px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.s3-info {
  padding: 20px;
}

.s3-files table {
  width: 100%;
  border-collapse: collapse;
}

.s3-files th,
.s3-files td {
  padding: 8px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.s3-files th {
  background-color: #f5f5f5;
}

.no-files {
  color: #666;
  font-style: italic;
}

.delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.delete-btn:hover {
  background-color: #ffebee;
}

.download-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background-color 0.2s;
    margin-right: 4px;
  }
  
.download-btn:hover {
  background-color: #e3f2fd;
}

.s3-files th:last-child,
.s3-files td:last-child {
  width: 100px;
  text-align: center;
}

.storage-info {
  font-size: 0.8em;
  color: #666;
  font-weight: normal;
  margin-left: 8px;
}

.sync-btn {
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;
}
.sync-btn:hover {
  background-color: #45a049;
}
</style>